import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentClinicId } from "@/hooks/useAppointments";
import {
  deriveFinancialStatusWithLegacyFallback,
  hasBillableAppointmentContent,
  hasOutstandingSaleReceivable,
} from "@/lib/utils/appointment-financial";
import { buildSuggestedPackageChargeFromAppointmentLink, type SuggestedPackageCharge } from "@/lib/utils/suggested-package-charge";

export interface FinalizedAppointmentPet {
  id: string;
  name: string;
}

export interface FinalizedAppointmentRow {
  id: string;
  starts_at: string;
  ends_at: string;
  type?: string | null;
  status?: string;
  vet_user_id: string | null;
  owner_id: string;
  pet_id: string;
  owner?: { name?: string };
  pet?: { name?: string };
  pets: FinalizedAppointmentPet[];
  vet?: { full_name?: string | null } | null;
  appointment_financial?: { status?: "pendente" | "pago" | "nao_cobrado" | null } | null;
  appointment_items?: FinalizedAppointmentItem[];
  suggested_package_charges?: SuggestedPackageCharge[];
  linked_sale?: {
    id: string;
    status?: string | null;
    sale_payments?: Array<{
      method?: string | null;
      amount?: number | null;
      received_at?: string | null;
    }> | null;
  } | null;
}

export interface FinalizedAppointmentItem {
  id: string;
  appointment_id: string;
  item_id: string;
  qty: number;
  title_snapshot: string;
  item?: { id: string; name: string; sale_price?: number | null; type?: string | null } | null;
}

const SALES_BATCH_SIZE = 40;

/**
 * Lista agendamentos com itens prontos para cobranca na clínica atual.
 * Mantém a venda vinculada para distinguir venda em aberto de venda paga.
 */
export function useFinalizedAppointments(options?: { limit?: number }) {
  const { data: clinicId } = useCurrentClinicId();
  const limit = options?.limit ?? 50;

  return useQuery({
    queryKey: ["finalized-appointments", clinicId, limit],
    queryFn: async () => {
      if (!clinicId) return { appointments: [], saleIdByAppointmentId: {} as Record<string, string> };
      const candidateLimit = Math.max(limit * 5, 200);
      const endOfToday = new Date();
      endOfToday.setHours(23, 59, 59, 999);
      const endOfTodayMs = endOfToday.getTime();

      const { data: appointments, error: appError } = await supabase
        .from("appointments")
        .select(`
          id,
          starts_at,
          ends_at,
          type,
          status,
          vet_user_id,
          owner_id,
          pet_id,
          clinic_appointment_type:clinic_appointment_types(id, name, color, icon, behavior),
          owner:owners!appointments_owner_id_fkey(name),
          pet:pets!appointments_pet_id_fkey(name),
          vet:profiles!appointments_vet_user_id_fkey(full_name),
          appointment_financial:appointment_financials(status),
          appointment_pets(pet:pets(id, name)),
          appointment_items(
            id,
            appointment_id,
            item_id,
            qty,
            title_snapshot,
            item:items(id, name, sale_price, type)
          ),
          appointment_patient_packages(
            id,
            pet_id,
            mode,
            billing_intent,
            suggested_charge_amount,
            catalog_package_id,
            patient_package_id,
            catalog_package:catalog_packages(
              id,
              controls_sessions,
              item:items(id, name, sale_price)
            ),
            patient_package:patient_packages(
              id,
              full_price,
              outstanding_amount,
              catalog_package:catalog_packages(
                id,
                controls_sessions,
                item:items(id, name, sale_price)
              )
            )
          )
        `)
        .eq("clinic_id", clinicId)
        .is("deleted_at", null)
        .order("starts_at", { ascending: false })
        .limit(candidateLimit);

      if (appError) throw appError;
      type RawRow = Omit<FinalizedAppointmentRow, "pets" | "owner" | "pet" | "appointment_financial"> & {
        owner?: { name?: string }[] | { name?: string };
        pet?: { name?: string }[] | { name?: string };
        appointment_financial?: { status?: "pendente" | "pago" | "nao_cobrado" | null } | { status?: "pendente" | "pago" | "nao_cobrado" | null }[] | null;
        appointment_pets?: Array<{ pet?: { id: string; name?: string } | null }>;
        appointment_items?: FinalizedAppointmentItem[];
        appointment_patient_packages?: Array<{
          id: string;
          pet_id: string;
          mode: "new" | "existing";
          billing_intent?: SuggestedPackageCharge["billingIntent"] | null;
          suggested_charge_amount?: number | null;
          catalog_package_id?: string | null;
          patient_package_id?: string | null;
          catalog_package?: {
            id: string;
            controls_sessions?: boolean | null;
            item?: { id?: string; name?: string | null; sale_price?: number | null } | null;
          } | null;
          patient_package?: {
            id: string;
            full_price?: number | null;
            outstanding_amount?: number | null;
            catalog_package?: {
              id: string;
              controls_sessions?: boolean | null;
              item?: { id?: string; name?: string | null; sale_price?: number | null } | null;
            } | null;
          } | null;
        }>;
      };
      const list = (appointments ?? []) as RawRow[];

      const appointmentIds = list.map((a) => a.id);
      if (appointmentIds.length === 0) {
        return { appointments: [], saleIdByAppointmentId: {} as Record<string, string> };
      }

      const sales: Array<{
        id: string;
        appointment_id: string | null;
        status?: string | null;
        sale_payments?: Array<{ method?: string | null; amount?: number | null; received_at?: string | null }> | null;
      }> = [];

      for (let index = 0; index < appointmentIds.length; index += SALES_BATCH_SIZE) {
        const batchIds = appointmentIds.slice(index, index + SALES_BATCH_SIZE);
        const { data: batchSales, error: salesError } = await supabase
          .from("sales")
          .select("id, appointment_id, status, sale_payments(method, amount, received_at)")
          .in("appointment_id", batchIds);

        if (salesError) throw salesError;
        sales.push(...(batchSales ?? []));
      }

      const saleIdByAppointmentId: Record<string, string> = {};
      const saleByAppointmentId: Record<string, NonNullable<FinalizedAppointmentRow["linked_sale"]>> = {};
      sales.forEach((s) => {
        if (!s.appointment_id) return;
        saleIdByAppointmentId[s.appointment_id] = s.id;
        saleByAppointmentId[s.appointment_id] = {
          id: s.id,
          status: s.status ?? null,
          sale_payments: s.sale_payments ?? [],
        };
      });

      const normalized = list.map((a) => {
        const owner = Array.isArray(a.owner) ? a.owner[0] : a.owner;
        const singlePet = Array.isArray(a.pet) ? a.pet[0] : a.pet;
        const appointmentFinancial = Array.isArray(a.appointment_financial)
          ? a.appointment_financial[0]
          : a.appointment_financial;
        const petsFromJunction: FinalizedAppointmentPet[] = (a.appointment_pets ?? [])
          .map((ap) => ap?.pet)
          .filter((p): p is { id: string; name?: string } => !!p && !!p.id)
          .map((p) => ({ id: p.id, name: p.name ?? "" }));
        const pets: FinalizedAppointmentPet[] =
          petsFromJunction.length > 0
            ? petsFromJunction
            : singlePet
              ? [{ id: a.pet_id, name: singlePet.name ?? "" }]
              : [];
        const pet = pets.length > 0
          ? (pets.find((p) => p.id === a.pet_id) ?? pets[0])
          : singlePet
            ? { id: a.pet_id, name: singlePet.name ?? "" }
            : undefined;
        return {
          ...a,
          owner,
          pet: pet ? { name: pet.name } : undefined,
          pets,
          appointment_financial: appointmentFinancial ?? null,
          appointment_items: a.appointment_items ?? [],
          linked_sale: saleByAppointmentId[a.id] ?? null,
          suggested_package_charges: (a.appointment_patient_packages ?? [])
            .filter((pkg) => {
              const intent = pkg.billing_intent ?? "undecided";
              return intent === "integral_now" || intent === "partial_now";
            })
            .map((pkg) => buildSuggestedPackageChargeFromAppointmentLink(pkg)),
        } as FinalizedAppointmentRow;
      }).filter((appointment) => {
        const startsAtMs = new Date(appointment.starts_at).getTime();
        const isUpToToday = Number.isFinite(startsAtMs) && startsAtMs <= endOfTodayMs;
        const hasLinkedSale = Boolean(saleIdByAppointmentId[appointment.id]);
        const hasOutstandingLinkedSale = hasOutstandingSaleReceivable(appointment.linked_sale);
        const isSettled =
          deriveFinancialStatusWithLegacyFallback(appointment.appointment_financial, {
            status: appointment.status ?? null,
          }, appointment.linked_sale) === "pago";
        const isBillable = hasBillableAppointmentContent({
          appointmentItems: appointment.appointment_items,
          packageCharges: appointment.suggested_package_charges,
        });

        return isUpToToday && (!hasLinkedSale || hasOutstandingLinkedSale) && !isSettled && isBillable;
      }).slice(0, limit);

      return { appointments: normalized, saleIdByAppointmentId };
    },
    enabled: !!clinicId,
    retry: false,
  });
}
